import type { PDFDocumentProxy } from "pdfjs-dist";
import React, { ComponentProps, useCallback, useRef, useState } from "react";
import { pdfjs, Document } from "react-pdf";

import { DocumentBody } from "../document-body/document-body";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import "./document-viewer.css";

// The PDF worker is copied pre-build as an npm script
pdfjs.GlobalWorkerOptions.workerSrc = `pdf.worker.min.js`;

export type DocumentViewerOptions = {
  /** Whether the document can be downloaded */
  canDownload: boolean;
};

// Unfortunately, the OnItemClick type is not exposed from react-pdf,
// so get a manual reference to it here.
type OnItemClick = NonNullable<ComponentProps<typeof Document>["onItemClick"]>;

export type DocumentViewerProps = {
  /** The URL to the PDF file */
  file: string;

  /** Whether the file required conversion or not */
  requiredConversion?: boolean;

  /** Additional options that control the document viewer's behaviour */
  viewerOptions: DocumentViewerOptions;

  /** Invoked when the user wants to download the original file */
  onDownloadOriginal: () => void;

  /** Invoked when the PDF could not be loaded for some reason */
  onLoadError: (err: Error) => void;
};

const options = {
  cMapUrl: "cmaps/",
  cMapPacked: true,
  standardFontDataUrl: "standard_fonts/",
};

/** The offset to use when scrolling to a page. This accounts for the toolbar + page padding */
const TOOLBAR_OFFSET = 74;

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  file,
  requiredConversion = false,
  viewerOptions,
  onDownloadOriginal,
  onLoadError,
}) => {
  const [pdf, setPdf] = useState<PDFDocumentProxy | undefined>(undefined);
  const [isSelectable, setSelectable] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Link click handler that scrolls to the target of the linked page.
   *
   * This ensures that the top of the target page is not shown underneath the toolbar.
   */
  const handleItemClick: OnItemClick = useCallback(({ pageNumber }) => {
    // Find the page container for the target page by querying the viewer container.
    // An alternative method would be to grab a reference to the `Document`
    // component as it exposes a `pages` object which caches all the registered
    // page containers. It's unclear whether this is intentional as:
    // - it's not exposed in its type information
    // - the `pages` object is a mutable ref object, which seems brittle to bind
    // Given that clicking a link isn't a frequent operation, it's probably OK
    // to query the DOM here.
    const element = containerRef.current?.querySelector(
      `[data-page-number="${pageNumber}"]`,
    );

    if (element) {
      // Use the instant behavior as there's an `IntersectionObserver` that would
      // otherwise try to load all intermediary pages when doing the slower smooth
      // scroll. See document-body.tsx for more information.
      element.scrollIntoView({ behavior: "instant" });

      // Scroll up a bit to account for the toolbar. Ideally, the scroll would be
      // done in a single operation, but the correct offset can't be calculated
      // reliably as some of the placeholders may not have fully retrieved their
      // page sizes.
      window.scrollBy(0, -1 * TOOLBAR_OFFSET);
    }
  }, []);

  return (
    <Document
      inputRef={containerRef}
      className={`pdf-viewer ${
        !isSelectable ? "pdf-viewer-not-selectable" : ""
      }`}
      file={file}
      renderMode="canvas"
      onLoadSuccess={async (pdf) => {
        setPdf(pdf);

        // LRN-203557: Prevent the user from selecting text when the PDF file does not permit it. Not that this is a
        // best-effort implementation and won't actually prevent the user from copying text if they're so inclined.
        const permissions = await pdf.getPermissions();
        if (permissions && !permissions.includes(pdfjs.PermissionFlag.COPY)) {
          setSelectable(false);
        }
      }}
      onLoadError={(err) => onLoadError(err)}
      onItemClick={handleItemClick}
      externalLinkTarget="_blank"
      imageResourcesPath="images/"
      options={options}
    >
      {pdf !== undefined ? (
        <DocumentBody
          pdf={pdf}
          viewerOptions={viewerOptions}
          requiredConversion={requiredConversion}
          onDownloadOriginal={onDownloadOriginal}
        />
      ) : undefined}
    </Document>
  );
};
